/**
 * Jobs Store
 * Manages jobs data for landing page
 * Separated from monolithic store following SOLID principles
 */

import { create } from 'zustand';
import { fetchJobs } from '../services/jobs.service';
import { globalConfig } from '../config/globalConfig';
import type { JobsStore } from '../interfaces/storeInterfaces/jobsStore';

export const useJobsStore = create<JobsStore>((set, get) => ({
  // Initial State
  jobs: [],
  pagination: null,
  isLoading: false,
  error: null,

  // Upload State
  isUploading: false,
  uploadProgress: 0,
  uploadError: null,

  // Actions
  load: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const requestParams = {
        page_number: params.page_number,
        page_size: params.page_size,
        ...(params.grade_id && { grade_id: params.grade_id }),
        ...(params.subject_id && { subject_id: params.subject_id }),
        ...(params.country_iso_code && { country_iso_code: params.country_iso_code }),
        ...(params.language_iso_code && { language_iso_code: params.language_iso_code }),
        ...(params.pending_graphics != null && { pending_graphics: params.pending_graphics }),
        ...(params.search_text && { search_text: params.search_text }),
        ...(params.search_type && { search_type: params.search_type }),
        ...(params.job_type && { job_type: params.job_type }),
      };

      const response = await fetchJobs(requestParams);
      set({
        jobs: response.data,
        pagination: response.pagination,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load jobs';
      set({ error: message, isLoading: false });
      console.error('Failed to load jobs:', error);
    }
  },

  reset: () => set({ jobs: [], pagination: null, isLoading: false, error: null }),

  // Upload Actions
  setUploading: (isUploading) => set({ isUploading }),

  setUploadProgress: (progress) => set({ uploadProgress: progress }),

  setUploadError: (error) => set({ uploadError: error }),

  resetUploadState: () => set({ 
    isUploading: false, 
    uploadProgress: 0, 
    uploadError: null 
  }),

  refreshJobs: async () => {
    const state = get();
    if (state.pagination) {
      // Reload jobs with current pagination settings
      await state.load({
        page_number: state.pagination.current_page || globalConfig.defaultPageNumber,
        page_size: state.pagination.page_size || globalConfig.defaultPageSize,
      });
    } else {
      // Initial load with defaults
      await state.load({
        page_number: globalConfig.defaultPageNumber,
        page_size: globalConfig.defaultPageSize,
      });
    }
  },
}));
